import { X, Download } from "lucide-react";
import { formatAmount } from "../../../utils/formatCurrency";
import { numberToWords } from "../../../utils/numberToWords";
import { calculateInvoiceSummary } from "../utils/invoiceSummary";
import { useState, useEffect } from "react";

/**
 * Full Invoice Preview Modal - 2025 UI/UX Standard
 * Stripe/Linear-grade polish, SleekBill-level completeness
 * Professional, calm, print-ready layout
 */
export default function FullInvoicePreview({ invoice, onClose, onDownload, type = "invoice" }) {
  const [companySettings, setCompanySettings] = useState(null);
  const [theme, setTheme] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null);
  const [brandColor, setBrandColor] = useState('#6366f1'); // Default indigo
  const [payments, setPayments] = useState([]);

  // Fetch payments for sent/paid invoices
  useEffect(() => {
    async function fetchPayments() {
      if ((invoice.status === 'sent' || invoice.status === 'paid') && invoice.id) {
        try {
          const { invoiceService } = await import('../../../services/invoice.service');
          const paymentData = await invoiceService.listPayments(invoice.id);
          setPayments(paymentData || []);
        } catch (err) {
          console.error('Failed to load payments for preview:', err);
        }
      }
    }
    fetchPayments();
  }, [invoice.id, invoice.status]);

  // Fetch company settings, theme, and convert logo
  useEffect(() => {
    async function fetchSettings() {
      try {
        const activeTheme = await window.electron.invoke('invoiceTheme:getActive');
        console.log('Active theme received:', activeTheme);
        setCompanySettings(activeTheme?.company || null);
        setTheme(activeTheme || null);
        
        // Set brand color
        if (activeTheme?.brandColor) {
          setBrandColor(activeTheme.brandColor);
          console.log('Brand color set to:', activeTheme.brandColor);
        }
        
        // Handle logo loading - support both file paths and data URLs
        if (activeTheme?.logoUrl) {
          try {
            console.log('Logo URL from theme:', activeTheme.logoUrl);
            
            // If it's already a data URL, use it directly
            if (activeTheme.logoUrl.startsWith('data:')) {
              setLogoBase64(activeTheme.logoUrl);
              console.log('Using data URL directly');
            } else {
              // Try to load via IPC for file paths
              console.log('Attempting to load logo via IPC from:', activeTheme.logoUrl);
              
              // Check if window.api.logo is available
              if (!window.api?.logo) {
                console.error('Logo IPC not available - window.api.logo is undefined');
                return;
              }
              
              // Try the new simple handler first, fallback to original if not available
              const logoHandler = window.api.logo.getBase64Simple || window.api.logo.getBase64;
              if (!logoHandler) {
                console.error('No logo handler available');
                return;
              }
              
              const logoResponse = await logoHandler(activeTheme.logoUrl);
              console.log('Logo response received:', logoResponse);
              if (logoResponse?.success && logoResponse?.base64) {
                setLogoBase64(logoResponse.base64);
                console.log('Logo loaded successfully via IPC');
              } else {
                console.error('Logo loading failed via IPC:', logoResponse?.error || 'Unknown error');
              }
            }
          } catch (error) {
            console.error('Failed to load logo:', error);
          }
        } else {
          console.log('No logo URL found in theme');
        }
      } catch (error) {
        console.error('Failed to fetch company settings:', error);
      }
    }
    fetchSettings();
  }, []);
  if (!invoice) return null;

  const items = invoice.items || [];
  const client = invoice.client;

  // Use calculated values from summary - this is the single source of truth
  const summary = calculateInvoiceSummary(invoice, companySettings);

  // Use the tax breakdown from summary (single source of truth)
  const { cgst, sgst, igst, sameState } = summary.taxBreakdown;
  const isSameState = sameState;
  
  // Extract other values from summary
  const subtotal = summary.subtotal;
  const taxTotal = summary.taxTotal;
  const total = summary.total;
  const balanceDue = summary.balanceDue;
  
  // Calculate discount amount based on type (for display purposes)
  const discountValue = invoice.discount || 0;
  const discountType = invoice.discountType || invoice.discount_type || 'flat';
  const discountAmount = discountType === 'percentage' 
    ? (subtotal * discountValue) / 100 
    : discountValue;
  
  const shipping = invoice.shipping || 0;
  const roundOff = invoice.round_off || invoice.roundOff || 0;

  // Status-based watermark
  const showWatermark = invoice.status === "draft";
  // Allow download at ANY stage - Modern SaaS UX
  const canDownload = true;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 backdrop-blur-lg animate-in fade-in duration-200">
      {/* Modal Container - Top-Tier 2026 Premium with Enhanced Floating Effect */}
      <div 
        className="bg-white w-full max-w-5xl h-[92vh] rounded-2xl flex flex-col overflow-hidden border border-slate-200/60 animate-in zoom-in-95 duration-300"
        style={{ 
          boxShadow: '0 30px 90px -20px rgba(0,0,0,0.45), 0 10px 30px -10px rgba(0,0,0,0.25)',
          animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        
        {/* Sticky Header - Premium 2026 Design */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-slate-200 bg-gradient-to-b from-white to-slate-50/50">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                  {invoice.invoice_number}
                </h2>
                <StatusBadge status={invoice.status} />
              </div>
              <p className="text-sm text-slate-600 mt-1 font-medium">
                {type === "quotation" ? "Quotation" : "Invoice"} Preview
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Download Button - Available at ANY stage */}
            <button
              onClick={onDownload}
              className="px-6 py-2.5 text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg transition-all shadow-sm hover:shadow-md hover:scale-[1.02] flex items-center gap-2.5"
            >
              <Download size={18} strokeWidth={2.5} />
              Download PDF
            </button>
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
              aria-label="Close preview"
            >
              <X size={22} strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Invoice Content - Smooth scrolling with refined background */}
        <div className="flex-1 overflow-auto bg-gradient-to-b from-slate-50/50 via-white to-slate-50/30 scroll-smooth">
          <div className="py-12 px-8">
            {/* A4 Paper Container - Premium with Soft Shadow */}
            <div 
              className="max-w-[800px] mx-auto bg-white rounded-2xl border border-slate-200/80 relative overflow-hidden"
              style={{ 
                boxShadow: '0 20px 60px -15px rgba(0,0,0,0.2), 0 8px 20px -8px rgba(0,0,0,0.1)'
              }}
            >
              {/* Draft Watermark */}
              {showWatermark && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                  <div className="text-9xl font-black text-slate-200/20 rotate-[-45deg] select-none tracking-wider">
                    DRAFT
                  </div>
                </div>
              )}

              <div className="p-16 pb-28 relative">{/* Increased bottom padding */}
                
                {/* Header Section - Match PDF Template Exactly */}
                <div className="mb-8">
                  {/* Header with gradient background and border like PDF */}
                  <div 
                    className="bg-gradient-to-b from-slate-50 to-white px-8 py-8 -mx-16 -mt-16 mb-8"
                    style={{ borderBottom: `4px solid ${brandColor}` }}
                  >
                    <div className="flex justify-between items-start">
                      {/* Left side - Company info with logo */}
                      <div className="flex-1">
                        {/* Company Logo with placeholder */}
                        {logoBase64 ? (
                          <div className="mb-4">
                            <img 
                              src={logoBase64} 
                              alt="Company Logo" 
                              className="max-w-[180px] max-h-[90px] object-contain"
                              onError={(e) => {
                                console.error('Logo failed to load:', e);
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => console.log('Logo loaded successfully')}
                            />
                          </div>
                        ) : theme?.logoUrl ? (
                          <div className="mb-4 flex items-center gap-2 text-slate-400">
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs">Loading logo...</span>
                          </div>
                        ) : null}
                        
                        {/* Company Name */}
                        <h1 className="text-3xl font-bold text-slate-900 mb-2 tracking-tight">
                          {companySettings?.name || 'ZyloBill'}
                        </h1>
                        
                        {/* Company Details */}
                        <div className="text-sm text-slate-600 leading-relaxed space-y-1">
                          {companySettings?.address && <div>{companySettings.address}</div>}
                          {(companySettings?.city || companySettings?.state || companySettings?.pincode) && (
                            <div>
                              {[companySettings.city, companySettings.state, companySettings.pincode]
                                .filter(Boolean)
                                .join(', ')}
                            </div>
                          )}
                          {companySettings?.gstin && (
                            <div className="mt-2">GSTIN: {companySettings.gstin}</div>
                          )}
                          <div className="mt-1">
                            {companySettings?.phone && <span>{companySettings.phone}</span>}
                            {companySettings?.phone && companySettings?.email && <span> • </span>}
                            {companySettings?.email && <span>{companySettings.email}</span>}
                          </div>
                        </div>
                      </div>

                      {/* Right side - Balanced visual weight with clean structure */}
                      <div className="text-right space-y-3.5">
                        {/* INVOICE title - Balanced size */}
                        <div 
                          className="text-4xl font-black tracking-tight leading-none"
                          style={{ color: brandColor }}
                        >
                          {type === "quotation" ? "QUOTATION" : "INVOICE"}
                        </div>
                        
                        {/* Invoice number with attached badge */}
                        <div className="space-y-2">
                          <div className="text-2xl font-black text-slate-900 tracking-tight">
                            {invoice.invoice_number}
                          </div>
                          <div className="flex items-center justify-end">
                            <StatusBadge status={invoice.status} />
                          </div>
                        </div>
                        
                        {/* Date information - Tightly grouped with subtle styling */}
                        <div className="text-sm text-slate-700 space-y-1.5 font-medium pt-2">
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-slate-500 text-xs">Issued:</span>
                            <span className="font-semibold">{formatDate(invoice.issueDate || invoice.issue_date)}</span>
                          </div>
                          <div className="flex items-center justify-end gap-2">
                            <span className="text-slate-500 text-xs">Due:</span>
                            <span className="font-semibold">{formatDate(invoice.dueDate || invoice.due_date)}</span>
                          </div>
                          {(invoice.poDate || invoice.po_date) && (
                            <div className="flex items-center justify-end gap-2 pt-2 mt-2 border-t border-slate-200">
                              <span className="text-slate-500 text-xs">PO Date:</span>
                              <span className="font-semibold">{formatDate(invoice.poDate || invoice.po_date)}</span>
                            </div>
                          )}
                          {(invoice.poNumber || invoice.po_number) && (
                            <div className="flex items-center justify-end gap-2">
                              <span className="text-slate-500 text-xs">PO Number:</span>
                              <span className="font-semibold">{invoice.poNumber || invoice.po_number}</span>
                            </div>
                          )}
                          {(invoice.agreementNumber || invoice.agreement_number) && (
                            <div className="flex items-center justify-end gap-2">
                              <span className="text-slate-500 text-xs">Agreement:</span>
                              <span className="font-semibold">{invoice.agreementNumber || invoice.agreement_number}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* FROM / BILL TO Section - Match PDF styling */}
                <div className="mb-8">
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                    <div className="grid grid-cols-2 gap-8">
                      {/* FROM column */}
                      <div>
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                          FROM
                        </div>
                        <div className="text-lg font-bold text-slate-900 mb-2">
                          {companySettings?.name || 'ZyloBill'}
                        </div>
                        <div className="text-sm text-slate-600 leading-relaxed">
                          {companySettings?.address && <div>{companySettings.address}</div>}
                          {(companySettings?.city || companySettings?.state || companySettings?.pincode) && (
                            <div>
                              {[companySettings.city, companySettings.state, companySettings.pincode]
                                .filter(Boolean)
                                .join(', ')}
                            </div>
                          )}
                          {companySettings?.gstin && (
                            <div className="mt-1">GSTIN: {companySettings.gstin}</div>
                          )}
                        </div>
                      </div>

                      {/* BILL TO column */}
                      <div className="border-l border-slate-200 pl-8">
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                          BILL TO
                        </div>
                        {client ? (
                          <>
                            <div className="text-lg font-bold text-slate-900 mb-2">{client.name}</div>
                            <div className="text-sm text-slate-600 leading-relaxed">
                              {client.billing_address && (
                                <div className="whitespace-pre-line mb-1">
                                  {client.billing_address}
                                </div>
                              )}
                              {(client.city || client.state || client.pincode) && (
                                <div className="mb-1">
                                  {[client.city, client.state, client.pincode].filter(Boolean).join(", ")}
                                </div>
                              )}
                              {client.gstin && (
                                <div className="mb-1">GSTIN: {client.gstin}</div>
                              )}
                              <div>
                                {client.email && <span>{client.email}</span>}
                                {client.email && client.phone && <span> • </span>}
                                {client.phone && <span>{client.phone}</span>}
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-slate-400 italic">No client selected</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Line Items Table - Enhanced Readability */}
                <div className="mb-8">
                  <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <table className="w-full text-sm">
                      <thead>
                        <tr 
                          className="bg-gradient-to-b from-slate-100 to-slate-50"
                          style={{ borderBottom: `3px solid ${brandColor}` }}
                        >
                          <th className="text-left py-4 px-4 font-bold text-slate-800 text-xs uppercase tracking-wider">Description</th>
                          <th className="text-center py-4 px-4 font-bold text-slate-800 text-xs uppercase tracking-wider">HSN/SAC</th>
                          <th className="text-center py-4 px-4 font-bold text-slate-800 text-xs uppercase tracking-wider">Qty</th>
                          <th className="text-right py-4 px-4 font-bold text-slate-800 text-xs uppercase tracking-wider">Rate</th>
                          {isSameState ? (
                            <>
                              <th className="text-right py-4 px-4 font-bold text-slate-800 text-xs uppercase tracking-wider">CGST</th>
                              <th className="text-right py-4 px-4 font-bold text-slate-800 text-xs uppercase tracking-wider">SGST</th>
                            </>
                          ) : (
                            <th className="text-right py-4 px-4 font-bold text-slate-800 text-xs uppercase tracking-wider">IGST</th>
                          )}
                          <th className="text-right py-4 px-4 font-bold text-slate-800 text-xs uppercase tracking-wider">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-100">
                        {items.map((item, index) => {
                          // Determine item type more robustly
                          const itemType = item.type || 
                                         (item.sac ? "service" : null) || 
                                         (item.hsn ? "product" : null) || 
                                         (invoice.type === "goods" ? "product" : "service");
                          const code = itemType === "product" ? item.hsn : item.sac;
                          
                          // Calculate item values using the same logic as summary
                          const itemQuantity = Number(item.quantity) || 0;
                          const itemRate = Number(item.rate) || 0;
                          const itemBase = itemQuantity * itemRate;
                          
                          // Calculate item discount
                          const itemDiscountType = item.discountType || 'flat';
                          const itemDiscountValue = Number(item.discount) || 0;
                          const itemDiscount = itemDiscountType === 'percentage' 
                            ? (itemBase * itemDiscountValue) / 100 
                            : itemDiscountValue;
                          
                          const itemAmount = Math.max(0, itemBase - itemDiscount);
                          
                          // Calculate item tax
                          const itemTaxRate = Number(item.gstRate || item.tax) || 0;
                          const itemTax = (itemAmount * itemTaxRate) / 100;
                          
                          let itemCgst = 0, itemSgst = 0, itemIgst = 0;
                          if (isSameState) {
                            itemCgst = itemTax / 2;
                            itemSgst = itemTax / 2;
                          } else {
                            itemIgst = itemTax;
                          }
                          
                          return (
                            <tr key={item.id || index} className="group hover:bg-slate-50/70 transition-colors duration-150">
                              <td className="py-5 px-4">
                                <div className="font-bold text-slate-900 text-sm leading-snug">{item.name}</div>
                                {item.description && (
                                  <div className="text-xs text-slate-600 mt-1.5 leading-relaxed">{item.description}</div>
                                )}
                              </td>
                              <td className="py-5 px-4 text-center text-slate-600 text-sm font-medium">
                                {code || "—"}
                              </td>
                              <td className="py-5 px-4 text-center text-slate-700 text-sm font-semibold">
                                {['year', 'month', 'quarter', 'week', 'day'].includes(item.uom) 
                                  ? `per ${item.uom}` 
                                  : `${itemQuantity} ${item.uom || 'Nos'}`}
                              </td>
                              <td className="py-5 px-4 text-right text-slate-700 text-sm font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                ₹{formatAmount(itemRate)}
                              </td>
                              {isSameState ? (
                                <>
                                  <td className="py-5 px-4 text-right text-slate-600 text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                    ₹{formatAmount(itemCgst)}
                                  </td>
                                  <td className="py-5 px-4 text-right text-slate-600 text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                    ₹{formatAmount(itemSgst)}
                                  </td>
                                </>
                              ) : (
                                <td className="py-5 px-4 text-right text-slate-600 text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  ₹{formatAmount(itemIgst)}
                                </td>
                              )}
                              <td className="py-5 px-4 text-right font-bold text-slate-900 text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                ₹{formatAmount(itemAmount)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Smooth transition to totals */}
                <div className="h-6"></div>

                {/* Totals Section - Premium Layered Depth with Hover + Payment Awareness */}
                <div className="flex justify-end mb-8">
                  <div className="w-96">
                    <div 
                      className="group bg-gradient-to-br from-slate-50 via-white to-slate-50 border-2 border-slate-200 rounded-xl p-8 transition-all duration-300 hover:shadow-[0_12px_35px_rgba(0,0,0,0.1),0_3px_8px_rgba(0,0,0,0.06)]"
                      style={{ 
                        boxShadow: '0 10px 30px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)'
                      }}
                    >
                      {/* Subtotal */}
                      <div className="flex justify-between py-3 text-sm">
                        <span className="font-semibold text-slate-700">Subtotal</span>
                        <span className="font-bold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{formatAmount(subtotal)}</span>
                      </div>

                      {/* Tax breakdown */}
                      {isSameState ? (
                        <>
                          <div className="flex justify-between py-3 text-sm">
                            <span className="font-semibold text-slate-700">CGST</span>
                            <span className="font-bold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{formatAmount(cgst)}</span>
                          </div>
                          <div className="flex justify-between py-3 text-sm">
                            <span className="font-semibold text-slate-700">SGST</span>
                            <span className="font-bold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{formatAmount(sgst)}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between py-3 text-sm">
                          <span className="font-semibold text-slate-700">IGST</span>
                          <span className="font-bold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{formatAmount(igst)}</span>
                        </div>
                      )}
                      
                      {/* Adjustments */}
                      {discountAmount > 0 && (
                        <div className="flex justify-between py-3 text-sm">
                          <span className="font-semibold text-slate-700">Discount</span>
                          <span className="font-bold text-red-600" style={{ fontVariantNumeric: 'tabular-nums' }}>−₹{formatAmount(discountAmount)}</span>
                        </div>
                      )}
                      
                      {shipping > 0 && (
                        <div className="flex justify-between py-3 text-sm">
                          <span className="font-semibold text-slate-700">Shipping</span>
                          <span className="font-bold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{formatAmount(shipping)}</span>
                        </div>
                      )}
                      
                      {roundOff !== 0 && (
                        <div className="flex justify-between py-3 text-sm">
                          <span className="font-semibold text-slate-700">Round Off</span>
                          <span className="font-bold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {roundOff > 0 ? '+' : '−'}₹{formatAmount(Math.abs(roundOff))}
                          </span>
                        </div>
                      )}

                      {/* Grand Total - Visually Dominant */}
                      <div 
                        className="flex justify-between items-center pt-6 mt-5"
                        style={{ borderTop: `4px solid ${brandColor}` }}
                      >
                        <span className="text-xl font-black text-slate-900 uppercase tracking-wide">Total</span>
                        <span 
                          className="text-4xl font-black tracking-tight"
                          style={{ color: brandColor, fontVariantNumeric: 'tabular-nums' }}
                        >
                          ₹{formatAmount(total)}
                        </span>
                      </div>

                      {/* Payment Summary - Only show for sent/paid invoices */}
                      {(invoice.status === 'sent' || invoice.status === 'paid') && (
                        <>
                          <div className="my-6 border-t-2 border-slate-200"></div>
                          
                          <div className="space-y-3">
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
                              Payment Summary
                            </div>
                            
                            {/* Amount Paid */}
                            <div className="flex justify-between py-2 text-sm">
                              <span className="font-semibold text-slate-700">Amount Paid</span>
                              <span className="font-bold text-emerald-600" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                ₹{formatAmount(invoice.total_paid || invoice.totalPaid || 0)}
                              </span>
                            </div>
                            
                            {/* Balance Remaining */}
                            <div className="flex justify-between py-2 text-sm">
                              <span className="font-semibold text-slate-700">Balance Remaining</span>
                              <span 
                                className={`font-bold ${balanceDue <= 0 ? 'text-emerald-600' : 'text-amber-600'}`}
                                style={{ fontVariantNumeric: 'tabular-nums' }}
                              >
                                ₹{formatAmount(balanceDue)}
                              </span>
                            </div>
                            
                            {/* Payment Status */}
                            <div className="flex justify-between items-center py-2 pt-4">
                              <span className="font-semibold text-slate-700 text-sm">Status</span>
                              <PaymentStatusBadge 
                                totalPaid={invoice.total_paid || invoice.totalPaid || 0} 
                                total={total}
                                balanceDue={balanceDue}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Amount in Words - Refined with Better Breathing Space */}
                <div 
                  className="mb-8 py-6 px-6 bg-gradient-to-br from-slate-50 to-slate-100/50 border-l-4 rounded-lg"
                  style={{ 
                    borderLeftColor: brandColor,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                  }}
                >
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">
                    Amount in Words
                  </div>
                  <div className="text-base font-bold text-slate-900 leading-relaxed">
                    {numberToWords(Math.floor(total))} rupees only
                  </div>
                </div>

                {/* Payment History - Only show if payments exist */}
                {payments.length > 0 && (
                  <div className="mb-8">
                    <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-6 shadow-sm">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
                        Payment History
                      </div>
                      <div className="space-y-3">
                        {payments.map((payment, index) => (
                          <div 
                            key={payment.id || index}
                            className="flex items-center justify-between py-3 px-4 bg-white border border-slate-100 rounded-lg hover:border-slate-200 transition-colors"
                          >
                            <div className="flex items-center gap-4 flex-1">
                              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-semibold text-slate-900">
                                    {formatDate(payment.payment_date)}
                                  </span>
                                  <span className="text-xs font-medium text-slate-500 capitalize">
                                    {formatPaymentMode(payment.mode)}
                                  </span>
                                </div>
                                {payment.notes && (
                                  <div className="text-xs text-slate-600 mt-1">{payment.notes}</div>
                                )}
                              </div>
                            </div>
                            <div className="text-sm font-bold text-emerald-600" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              ₹{formatAmount(Number(payment.amount))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {invoice.notes && (
                  <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                    <div className="text-xs font-bold text-yellow-800 uppercase tracking-wider mb-2">
                      Notes
                    </div>
                    <div className="text-sm text-yellow-900 leading-relaxed whitespace-pre-line">
                      {invoice.notes}
                    </div>
                  </div>
                )}

                {/* Payment Instructions */}
                {theme?.paymentInstructions && (
                  <div 
                    className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-md"
                    style={{ borderLeft: `4px solid ${brandColor}` }}
                  >
                    <div className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-2">
                      Payment Instructions
                    </div>
                    <div className="text-sm text-blue-900 leading-relaxed whitespace-pre-line">
                      {theme.paymentInstructions}
                    </div>
                  </div>
                )}

                {/* Terms and Conditions */}
                {theme?.termsAndConditions && (
                  <div className="mb-8 p-4 bg-slate-50 border border-slate-200 rounded-md">
                    <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
                      Terms & Conditions
                    </div>
                    <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                      {theme.termsAndConditions}
                    </div>
                  </div>
                )}

                {/* Divider - Clear Separation with Increased Spacing */}
                <div className="relative my-12">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-300"></div>
                  </div>
                </div>

                {/* Footer - Refined with Better Readability */}
                <div className="text-center space-y-3 pt-2">
                  <div className="max-w-2xl mx-auto text-sm text-slate-700 leading-relaxed font-medium">
                    {theme?.footerText || theme?.footerNote || 'This is a computer-generated invoice and does not require a signature.'}
                  </div>
                  {theme?.thankYouMessage && (
                    <div className="text-sm text-slate-600 font-medium">
                      {theme.thankYouMessage}
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────── Helper Components - Premium 2026 Design ───────── */

function StatusBadge({ status }) {
  const styles = {
    draft: "bg-slate-100 text-slate-700 border-slate-300 shadow-sm",
    sent: "bg-blue-50 text-blue-700 border-blue-300 shadow-sm",
    paid: "bg-emerald-50 text-emerald-700 border-emerald-300 shadow-sm",
    partially_paid: "bg-indigo-50 text-indigo-700 border-indigo-300 shadow-sm",
    cancelled: "bg-slate-100 text-slate-600 border-slate-300 shadow-sm",
  };

  const dots = {
    draft: "bg-slate-500",
    sent: "bg-blue-600",
    paid: "bg-emerald-600",
    partially_paid: "bg-indigo-600",
    cancelled: "bg-slate-500",
  };

  const displayLabel = status === "partially_paid" ? "Partial" : status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border ${styles[status] || styles.draft}`}>
      <span className={`w-2 h-2 rounded-full ${dots[status] || dots.draft}`}></span>
      {displayLabel}
    </span>
  );
}

function PaymentStatusBadge({ totalPaid, total, balanceDue }) {
  let status, label, bgColor, textColor, dotColor;
  
  if (balanceDue <= 0 && totalPaid > 0) {
    // Fully paid
    status = 'paid';
    label = 'Paid in Full';
    bgColor = 'bg-emerald-50';
    textColor = 'text-emerald-700';
    dotColor = 'bg-emerald-600';
  } else if (totalPaid > 0) {
    // Partially paid
    status = 'partial';
    label = 'Partially Paid';
    bgColor = 'bg-amber-50';
    textColor = 'text-amber-700';
    dotColor = 'bg-amber-600';
  } else {
    // Unpaid
    status = 'unpaid';
    label = 'Unpaid';
    bgColor = 'bg-slate-100';
    textColor = 'text-slate-600';
    dotColor = 'bg-slate-400';
  }

  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-200 ${bgColor} ${textColor}`}>
      <span className={`w-2 h-2 rounded-full ${dotColor}`}></span>
      {label}
    </span>
  );
}

function formatDate(dateString) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatPaymentMode(mode) {
  const modes = {
    cash: "Cash",
    bank_transfer: "Bank Transfer",
    cheque: "Cheque",
    upi: "UPI",
    card: "Card",
    other: "Other",
  };
  return modes[mode] || mode;
}
